import axios from 'axios';
import { apiClient } from '@/services/_shared/apiClient';
import ENDPOINTS from '@/utils/endpoints';

// `axios` (default import) is used ONLY for the `axios.isAxiosError` type
// guard on the reject-content flow — pure static helper, no interceptor
// side effects. All actual HTTP calls go through `apiClient`.

/* ── Types ── */

export interface CreateContentOrderPayload {
  title: string;
  word_count: 800 | 1000 | 1200 | 1500 | 2000;
  content_brief?: string;
  keywords?: string[];
  target_url?: string;
  anchor_text?: string;
  priority?: 'normal' | 'urgent';
  advertiser_notes?: string;
}

export interface TimelineEvent {
  id: string;
  step: string;
  label: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  sort_order: number;
  started_at: string | null;
  completed_at: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
}

export interface ContentDelivery {
  id: string;
  order_id: string;
  version: number;
  title: string;
  body: string;
  word_count: number;
  submitted_by: string;
  status: 'submitted' | 'approved' | 'rejected';
  reviewer_id: string | null;
  reviewer_notes: string | null;
  reviewed_at: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export interface ContentOrderData {
  id: string;
  order_number: string;
  order_type: 'content';
  title: string;
  status: string;
  progress_pct: number;
  priority: string;
  word_count: number;
  content_brief: string | null;
  keywords: string[] | null;
  target_url: string | null;
  anchor_text: string | null;
  content_by: string | null;
  total_amount: number;
  writer_payout: number;
  platform_fee: number;
  currency: string;
  payment_status: string;
  paid_at: string | null;
  escrow_id: string | null;
  revision_count: number;
  revisions_allowed: boolean;
  advertiser_notes: string | null;
  due_date: string | null;
  confirmed_at: string | null;
  started_at: string | null;
  expected_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  timeline_events?: TimelineEvent[];
  contents?: ContentDelivery[];
  site?: { id: string; name: string; domain: string } | null;
  created_at: string;
  updated_at: string;
}

export interface ContentOrdersListResponse {
  success: boolean;
  message: string;
  data: {
    data: ContentOrderData[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
  };
}

export interface ContentOrdersListParams {
  status?: string;
  priority?: string;
  payment_status?: string;
  created_from?: string;
  created_to?: string;
  due_from?: string;
  due_to?: string;
  min_amount?: number;
  max_amount?: number;
  overdue?: boolean;
  sla_breached?: boolean;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}

/* ── API Functions ── */

/**
 * GET /v1/content-orders/my-orders — Advertiser's content orders list
 */
export async function fetchMyContentOrders(
  params: ContentOrdersListParams = {}
): Promise<ContentOrdersListResponse> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const query: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      query[key] = value;
    }
  }
  const response = await apiClient.get<ContentOrdersListResponse>(ENDPOINTS.CONTENT_ORDERS_MINE, {
    params: query,
  });
  return response.data;
}

/**
 * GET /v1/content-orders/{id} — Content order detail (includes timeline + contents)
 */
export async function fetchContentOrderDetail(
  id: string
): Promise<{ success: boolean; message: string; data: ContentOrderData }> {
  const response = await apiClient.get(ENDPOINTS.CONTENT_ORDER_DETAIL(id));
  return response.data;
}

/**
 * POST /v1/content-orders — Create a new content order
 */
export async function createContentOrder(
  payload: CreateContentOrderPayload
): Promise<{ success: boolean; message: string; data: ContentOrderData }> {
  const response = await apiClient.post(ENDPOINTS.CONTENT_ORDERS, payload);
  return response.data;
}

/**
 * POST /v1/content-orders/{id}/pay
 */
export async function payContentOrder(id: string): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post(ENDPOINTS.CONTENT_ORDER_PAY(id));
  return response.data;
}

/**
 * POST /v1/content-orders/{id}/cancel
 */
export async function cancelContentOrder(
  id: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post(
    ENDPOINTS.CONTENT_ORDER_CANCEL(id),
    reason ? { reason } : {}
  );
  return response.data;
}

/**
 * GET /v1/content-orders/{id}/timeline
 */
export async function fetchContentOrderTimeline(
  id: string
): Promise<{ success: boolean; data: TimelineEvent[] }> {
  const response = await apiClient.get(ENDPOINTS.CONTENT_ORDER_TIMELINE(id));
  return response.data;
}

/**
 * GET /v1/content-orders/{id}/history
 */
export async function fetchContentOrderHistory(id: string): Promise<{
  success: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
}> {
  const response = await apiClient.get(ENDPOINTS.CONTENT_ORDER_HISTORY(id));
  return response.data;
}

/**
 * GET /v1/content-orders/{id}/contents — List all content versions
 */
export async function fetchContentDeliveries(
  id: string
): Promise<{ success: boolean; data: ContentDelivery[] }> {
  const response = await apiClient.get(ENDPOINTS.CONTENT_ORDER_CONTENTS(id));
  return response.data;
}

/**
 * Warning surfaced by the Copyleaks AI gate on advertiser-submitted content.
 * Score = probability of AI generation; threshold = platform maximum. When
 * the score meets/exceeds the threshold AND the advertiser hasn't yet
 * acknowledged, the backend returns 422 with `requires_confirmation: true`
 * and this warning payload — thrown as `CopyleaksAiWarningError` below.
 */
export interface CopyleaksWarning {
  type: 'ai';
  score: number;
  threshold: number;
  message: string;
}

/**
 * Thrown by `submitContentDelivery` when the backend returns 422 with
 * `requires_confirmation: true`. Callers open a confirm dialog showing
 * `warnings[0].message` + a "Continue anyway" button; the second submit
 * passes `warningsAcknowledged: true` to bypass the popup.
 */
export class CopyleaksAiWarningError extends Error {
  readonly warnings: CopyleaksWarning[];
  readonly aiReport: Record<string, unknown>;

  constructor(warnings: CopyleaksWarning[], aiReport: Record<string, unknown>) {
    super(warnings[0]?.message ?? 'Content flagged as AI-likely by Copyleaks.');
    this.name = 'CopyleaksAiWarningError';
    this.warnings = warnings;
    this.aiReport = aiReport;
  }
}

/**
 * POST /v1/content-orders/{id}/contents — Submit a new content version.
 *
 * The backend runs the Copyleaks AI gate on advertiser content submits.
 * On the confirm-required response (422 + `requires_confirmation: true`),
 * we throw `CopyleaksAiWarningError`. Callers should catch it, show the
 * "AI likelihood X% exceeds threshold Y% — Continue anyway?" dialog, and
 * re-invoke this function with `warningsAcknowledged: true`.
 */
export async function submitContentDelivery(
  id: string,
  body: { title: string; body: string; word_count: number; warningsAcknowledged?: boolean }
): Promise<{ success: boolean; message: string; data: ContentDelivery }> {
  const { warningsAcknowledged, ...rest } = body;
  try {
    const response = await apiClient.post(ENDPOINTS.CONTENT_ORDER_CONTENTS(id), {
      ...rest,
      warnings_acknowledged: warningsAcknowledged ?? false,
    });
    return response.data;
  } catch (err: unknown) {
    // Axios attaches the response to the error. If the backend returned
    // the Copyleaks confirm-required shape, translate to a typed error.
    const status = (
      err as {
        response?: {
          status?: number;
          data?: {
            requires_confirmation?: boolean;
            warnings?: CopyleaksWarning[];
            ai_report?: Record<string, unknown>;
          };
        };
      }
    )?.response;
    if (status?.status === 422 && status.data?.requires_confirmation === true) {
      throw new CopyleaksAiWarningError(status.data.warnings ?? [], status.data.ai_report ?? {});
    }
    throw err;
  }
}

/**
 * POST /v1/orders/{orderId}/contents/{contentId}/approve
 */
export async function approveContentVersion(
  orderId: string,
  contentId: string
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post(ENDPOINTS.CONTENT_VERSION_APPROVE(orderId, contentId));
  return response.data;
}

/**
 * POST /v1/orders/{orderId}/contents/{contentId}/reject
 *
 * On the platform's `order.max_revisions` cap being exceeded, throws
 * `RevisionCapExceededError` (imported from orderService) so callers can
 * toast the exact backend message instead of a generic "Failed to submit".
 */
export async function rejectContentVersion(
  orderId: string,
  contentId: string,
  notes: string
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await apiClient.post(ENDPOINTS.CONTENT_VERSION_REJECT(orderId, contentId), {
      notes,
    });
    return response.data;
  } catch (err) {
    // Detect the enforceRevisionCap 422 shape and rethrow as typed error.
    if (axios.isAxiosError(err) && err.response?.status === 422) {
      const body = err.response.data as
        | {
            message?: string;
            errors?: { revision_notes?: unknown; content_ai_probability?: unknown };
            meta?: { max_revisions?: number; revision_count?: number };
          }
        | undefined;
      const isCap =
        body?.errors &&
        'revision_notes' in body.errors &&
        !('content_ai_probability' in body.errors);
      if (isCap && body?.message) {
        // Import at use-site to avoid a circular dep with orderService.
        const { RevisionCapExceededError } = await import('./orderService');
        throw new RevisionCapExceededError(body.message, body.meta);
      }
    }
    throw err;
  }
}
